import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Pagination,
  Modal,
  Table,
} from "react-bootstrap";
import {
  FaClipboardList,
  FaClock,
  FaQuestionCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaPlay,
  FaCalendarAlt,
  FaHourglassHalf,
  FaHistory,
  FaEye,
  FaRedo
} from "react-icons/fa";
import { toast } from "react-toastify";
import Header from "../../components/common/Header";
import TestService from "../../services/testService";
import "../../styles/student-tests.css";

const StudentTests = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Pagination
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedTestHistory, setSelectedTestHistory] = useState(null);

  useEffect(() => {
    loadTests();
  }, [currentPage]);

  const loadTests = async () => {
    try {
      setLoading(true);
      const response = await TestService.getMyTests(currentPage, 10);
      console.log("Tests loaded:", response.data);
      setTests(response.data?.data || []);
      if (response.data?.pagination) {
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error("Failed to load tests:", error);
      toast.error("Failed to load tests");
    } finally {
      setLoading(false);
    }
  };

  const handleStartTest = async (test) => {
    console.log("Starting test with ID:", test.id);
    console.log("Test schedule:", test.start_date, test.end_date);

    // Helper function to get timestamp
    const getTimestamp = (dateStr) => {
      if (!dateStr) return null;
      return new Date(dateStr).getTime();
    };

    // Check schedule before starting using timestamps
    const now = new Date().getTime();
    const startTimestamp = getTimestamp(test.start_date);
    const endTimestamp = getTimestamp(test.end_date);

    console.log("Current timestamp:", now);
    console.log("Start timestamp:", startTimestamp);
    console.log("End timestamp:", endTimestamp);

    if (startTimestamp && now < startTimestamp) {
      const startDateTime = formatDateTime(test.start_date);
      toast.warning(`⏰ This test will be available on ${startDateTime}`);
      return;
    }

    if (endTimestamp && now > endTimestamp) {
      toast.error(`❌ This test expired on ${formatDateTime(test.end_date)}`);
      return;
    }

    if (test.attempt_status === "in_progress") {
      navigate(`/student/take-test/${test.attempt_id}`);
    } else {
      try {
        const response = await TestService.startTest(test.id);
        console.log("Start test response:", response.data);

        if (response.data && response.data.attempt_id) {
          navigate(`/student/take-test/${response.data.attempt_id}`);
        } else {
          toast.error("Failed to start test: Invalid response");
        }
      } catch (error) {
        console.error("Failed to start test:", error);

        // Get the actual error message from backend
        let errorMessage = "Failed to start test";

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        console.log("Backend error message:", errorMessage);

        // Show the error message prominently
        if (errorMessage.includes("starts on")) {
          toast.warning(errorMessage);
        } else if (errorMessage.includes("ended on")) {
          toast.error(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      }
    }
  };

  const handleViewResult = (attemptId) => {
    navigate(`/student/test-result/${attemptId}`);
  };

  const openHistoryModal = (test) => {
    setSelectedTestHistory(test);
    setShowHistoryModal(true);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Get schedule status for a test using timestamps
  const getScheduleStatus = (test) => {
    const now = new Date().getTime();
    const startTimestamp = test.start_date ? new Date(test.start_date).getTime() : null;
    const endTimestamp = test.end_date ? new Date(test.end_date).getTime() : null;

    if (startTimestamp && now < startTimestamp) {
      return {
        status: "upcoming",
        label: "Upcoming",
        variant: "info",
        icon: <FaCalendarAlt />,
      };
    }
    if (endTimestamp && now > endTimestamp) {
      return {
        status: "expired",
        label: "Expired",
        variant: "secondary",
        icon: <FaHourglassHalf />,
      };
    }
    return {
      status: "active",
      label: "Active",
      variant: "success",
      icon: <FaPlay />,
    };
  };

  const getStatusBadge = (test) => {
    // First check schedule status
    const schedule = getScheduleStatus(test);

    if (schedule.status === "upcoming") {
      return <Badge bg="info">📅 Upcoming</Badge>;
    }

    if (schedule.status === "expired") {
      return <Badge bg="secondary">⏰ Expired</Badge>;
    }

    // If test is active, show attempt status
    if (test.attempt_status === "completed") {
      const percentage = parseFloat(test.percentage) || 0;
      return test.passed ? (
        <Badge bg="success">
          <FaCheckCircle className="me-1" /> Passed ({percentage}%)
        </Badge>
      ) : (
        <Badge bg="danger">
          <FaTimesCircle className="me-1" /> Failed ({percentage}%)
        </Badge>
      );
    } else if (test.attempt_status === "in_progress") {
      return <Badge bg="warning">In Progress</Badge>;
    }
    return <Badge bg="primary">Ready to Start</Badge>;
  };

  const isTestAvailable = (test) => {
    const now = new Date().getTime();
    const startTimestamp = test.start_date ? new Date(test.start_date).getTime() : null;
    const endTimestamp = test.end_date ? new Date(test.end_date).getTime() : null;

    if (startTimestamp && now < startTimestamp) return false;
    if (endTimestamp && now > endTimestamp) return false;
    return true;
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setCurrentPage(pageNumber);
    }
  };

  const renderPagination = () => {
    const { current_page, total_pages } = pagination;
    if (total_pages <= 1) return null;

    let items = [];
    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    for (let i = 1; i <= total_pages; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return <Pagination>{items}</Pagination>;
  };

  const formatPercentage = (percentage) => {
    if (percentage === null || percentage === undefined) return "0";
    const num = parseFloat(percentage);
    return isNaN(num) ? "0" : num.toFixed(2);
  };

  if (loading) {
    return (
      <div>
        <Header />
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading tests...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div className="student-tests-page">
        <Container className="py-5">
          <div className="text-center mb-5">
            <h1 className="display-5 fw-bold">
              <FaClipboardList className="me-3 text-primary" />
              My Tests
            </h1>
            <p className="text-muted">
              Attempt your assigned tests and track your progress
            </p>
          </div>

          {tests.length === 0 ? (
            <Alert variant="info" className="text-center py-5">
              <FaClipboardList size={50} className="mb-3 text-muted" />
              <h5>No tests assigned yet</h5>
              <p>You don't have any tests assigned at the moment.</p>
            </Alert>
          ) : (
            <>
              <Row className="g-4">
                {tests.map((test) => {
                  const schedule = getScheduleStatus(test);
                  const isAvailable = isTestAvailable(test);

                  return (
                    <Col md={6} lg={4} key={test.id}>
                      <Card className="h-100 shadow-sm test-card">
                        <Card.Body>
                          <div className="test-status-badge">
                            {getStatusBadge(test)}
                          </div>
                          <h5 className="card-title mt-3">{test.title}</h5>
                          <p className="card-text text-muted small">
                            {test.description?.substring(0, 100) ||
                              "No description"}
                          </p>

                          {/* Schedule Information */}
                          {(test.start_date || test.end_date) && (
                            <div className="schedule-info mb-2 p-2 bg-light rounded">
                              {test.start_date && (
                                <div className="small">
                                  <FaCalendarAlt className="me-1 text-primary" />
                                  <strong>Starts:</strong>{" "}
                                  {formatDateTime(test.start_date)}
                                </div>
                              )}
                              {test.end_date && (
                                <div className="small mt-1">
                                  <FaHourglassHalf className="me-1 text-warning" />
                                  <strong>Ends:</strong>{" "}
                                  {formatDateTime(test.end_date)}
                                </div>
                              )}
                            </div>
                          )}

                          <div className="test-meta">
                            <div className="meta-item">
                              <FaQuestionCircle className="me-1" />
                              {test.total_questions} Questions
                            </div>
                            <div className="meta-item">
                              <FaClock className="me-1" />
                              {test.duration_minutes} min
                            </div>
                            <div className="meta-item">
                              Passing: {test.passing_percentage}%
                            </div>
                          </div>

                          {test.attempt_status === "completed" && (
                            <div className="test-score mt-2">
                              <strong>Your Score:</strong>{" "}
                              <span
                                className={
                                  test.passed ? "text-success" : "text-danger"
                                }
                              >
                                {formatPercentage(test.percentage)}%
                              </span>
                            </div>
                          )}
                        </Card.Body>
                        <Card.Footer className="bg-transparent border-0 pb-3">
                          {test.attempt_status === "completed" ? (
                            <div className="d-flex flex-column gap-2">
                              {test.attempt_history && test.attempt_history.length > 1 ? (
                                <Button
                                  variant="outline-primary"
                                  className="w-100"
                                  onClick={() => openHistoryModal(test)}
                                >
                                  <FaHistory className="me-2" /> View History
                                </Button>
                              ) : (
                                <Button
                                  variant="outline-primary"
                                  className="w-100"
                                  onClick={() => handleViewResult(test.attempt_id)}
                                >
                                  <FaEye className="me-2" /> View Result
                                </Button>
                              )}
                              
                              {test.can_retake && (
                                <Button
                                  variant="primary"
                                  className="w-100 mt-2"
                                  onClick={() => handleStartTest(test)}
                                  disabled={!isAvailable}
                                >
                                  <FaRedo className="me-2" /> Retake Test
                                </Button>
                              )}
                            </div>
                          ) : schedule.status === "upcoming" ? (
                            <Button
                              variant="secondary"
                              className="w-100"
                              disabled
                            >
                              <FaCalendarAlt className="me-2" />
                              Not Started Yet
                            </Button>
                          ) : schedule.status === "expired" ? (
                            <Button
                              variant="secondary"
                              className="w-100"
                              disabled
                            >
                              <FaHourglassHalf className="me-2" />
                              Test Expired
                            </Button>
                          ) : (
                            <Button
                              variant="primary"
                              className="w-100"
                              onClick={() => handleStartTest(test)}
                              disabled={!isAvailable}
                            >
                              <FaPlay className="me-2" />
                              {test.attempt_status === "in_progress"
                                ? "Resume Test"
                                : "Start Test"}
                            </Button>
                          )}
                        </Card.Footer>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
              <div className="d-flex justify-content-center mt-4">
                {renderPagination()}
              </div>
            </>
          )}

          {/* History Modal */}
          <Modal
            show={showHistoryModal}
            onHide={() => setShowHistoryModal(false)}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>
                Attempt History: {selectedTestHistory?.title}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Table responsive striped bordered hover className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Attempt</th>
                    <th>Date</th>
                    <th>Score</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedTestHistory?.attempt_history?.map((attempt) => (
                    <tr key={attempt.id}>
                      <td>#{attempt.attempt_number}</td>
                      <td>{formatDateTime(attempt.completed_at || attempt.started_at)}</td>
                      <td>
                        {attempt.status === 'completed' ? (
                          <span className={attempt.passed ? "text-success fw-bold" : "text-danger fw-bold"}>
                            {formatPercentage(attempt.percentage)}%
                          </span>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {attempt.status === 'completed' ? (
                          <Badge bg={attempt.passed ? "success" : "danger"}>
                            {attempt.passed ? "Passed" : "Failed"}
                          </Badge>
                        ) : (
                          <Badge bg="warning">In Progress</Badge>
                        )}
                      </td>
                      <td>
                        {attempt.status === 'completed' && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setShowHistoryModal(false);
                              handleViewResult(attempt.id);
                            }}
                          >
                            <FaEye /> View
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </div>
  );
};

export default StudentTests;